import { createContext, useContext, type ComponentType, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

export interface SectionPage {
  name: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  /** True for the page that is open. */
  current: boolean;
}

export interface OpenSectionPages {
  /** The section's name as the menu has it: "My Approvals". */
  section: string;
  /** The section's pages this person holds, in menu order. */
  pages: SectionPage[];
}

/**
 * The pages of the section a person is in, for a section that has no front page (My
 * Approvals): the top bar opens its first page, and these are the way to the others.
 * DashboardLayout knows them from the person's menu and hands them to the page it
 * shows; null everywhere else (a section with a front page, the Dashboard, an address
 * the menu cannot place).
 */
export const SectionPagesContext = createContext<OpenSectionPages | null>(null);

/**
 * The section's pages as tabs in the page itself, under its title: the master-page strip
 * (light tray, the open page a white pill, an icon before each name), each tab a link to
 * its page. Draws nothing outside such a section, or for a person who holds fewer than two
 * of its pages. The strip wraps onto more lines on a narrow screen instead of running off it.
 */
export default function SectionPages(): ReactElement | null {
  const open = useContext(SectionPagesContext);
  if (!open || open.pages.length < 2) return null;
  return (
    <nav aria-label={`Pages in ${open.section}`} className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex w-fit max-w-full flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
        {open.pages.map((page, i) => {
          const Icon = page.icon;
          return (
            <Link
              key={`${i}-${page.to}`}
              to={page.to}
              aria-current={page.current ? 'page' : undefined}
              className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                page.current ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {page.name}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
